import React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Loading } from "@common/components/loading";
import { Options, OptionsItem } from "./components/options";
import { HttpErrorDto } from "@common/dto/http";

import "./item-security-management.styles.scss";

export type ItemSecurityManagementOption = OptionsItem;

export type ItemSecurityManagementProps = {
  getId?: {
    title?: string;
    cancelBtn?: string;
    submitBtn?: string;
    checkbox?: (params: { item: OptionsItem, type: 'read' | 'write'; rowIndex: number; columnIndex: number }) => string;
    cell?: (params: { item: OptionsItem; rowIndex: number; columnIndex: number }) => string;
  };
  title: string;
  overriddenSubtitle?: React.ReactNode;
  onClose: () => void;
  getSecurity: () => Promise<{ items: Array<ItemSecurityManagementOption>; overridden: boolean }>;
  showErrorMessage: (error: string) => void;
  onSubmit: (securityOptions: Array<ItemSecurityManagementOption>) => Promise<void>;
};

export const ItemSecurityManagement: React.FC<ItemSecurityManagementProps> = (props) => {
  const { onClose, onSubmit, showErrorMessage, getSecurity, title, getId, overriddenSubtitle } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [initializing, setInitializingState] = React.useState(true);
  const [securityOptions, setSecurityOptions] = React.useState<Array<ItemSecurityManagementOption>>([]);
  const [overridden, setOverridden] = React.useState<boolean>(false);

  React.useEffect(() => {
    setInitializingState(true);
    getSecurity()
      .then((data) => {
        setSecurityOptions(data.items);
        setOverridden(data.overridden);
      })
      .catch((err) => {
        showErrorMessage(err?.entity || I18n.t("Failed to get security information"));
        console.error(err);
      })
      .finally(() => {
        setInitializingState(false);
      });
  }, []);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      await onSubmit(securityOptions);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Operation failed"));
    }
  }, [onSubmit, showErrorMessage, securityOptions]);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="ItemSecurityManagement"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-ped-sec-close' })}
        >
          {title}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializing 
          ? <Flex className="ItemSecurityManagement__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : (
            <>
              {overridden && overriddenSubtitle && <h6 className="ItemSecurityManagement__overridden-msg">{overriddenSubtitle}</h6>}
              <Options
                getId={getId}
                disabled={overridden}
                data={securityOptions}
                onChange={setSecurityOptions}
              />
            </>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes({ id: getId?.cancelBtn ?? 'btn-sec-cancel' })}
          onClick={onClose}
        >
          {I18n.t("Cancel")}
        </Button>
        {!overridden && (
          <Button
            disabled={submitting}
            bsStyle="primary"
            {...getTestAttributes({ id: getId?.submitBtn ?? 'btn-sec-save' })}
            onClick={handleSubmit}
          >
            {submitting ? I18n.t("Loading") : I18n.t('Save')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
